import { createSlice } from "@reduxjs/toolkit";
import {
  getDoctorProfile,
  togglePatientVisibility,
  updateDoctorProfile,
} from "../../reducer/doctorProfile/doctorProfileReducer";
import { uploadDoctorAvatar } from "../../reducer/uploadDoctorAvatar/uploadDoctorAvatarReducer";
import { updateAcceptInsuranceOrders } from "../../reducer/acceptInsuranceOrders/updateAcceptInsuranceOrdersReducer";

const doctorProfileSlice = createSlice({
  name: "doctorProfile",
  initialState: {
    doctor: {},
    loading: false,
    error: null,
    showPatientCount: false,
    avatarUrl: null,
    rating: 0,
    // "Accept insurance-covered orders", kept apart from `doctor` on purpose: changing it must
    // not replace the `doctor` object, which the profile form reloads all its fields from.
    acceptInsuranceOrders: false,
    acceptInsuranceSaving: false,
  },
  reducers: {
    // Set avatar preview immediately when file is selected
    setAvatarPreview: (state, action) => {
      state.avatarUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔹 GET Doctor Profile
      .addCase(getDoctorProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDoctorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.doctor = action.payload;
        state.showPatientCount =
          action.payload?.doctor?.show_patient_count || false;
        state.avatarUrl = action.payload?.user?.avatar_presigned_url || null;
        state.rating = action.payload?.doctor?.rating || 0;
        state.acceptInsuranceOrders =
          !!action.payload?.doctor?.accept_insurance_orders;
      })
      .addCase(getDoctorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔹 UPDATE Doctor Profile
      .addCase(updateDoctorProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDoctorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.doctor = action.payload;
        state.avatarUrl = action.payload?.user?.avatar_presigned_url || null;
        state.rating = action.payload?.doctor?.rating || 0;
      })
      .addCase(updateDoctorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔹 TOGGLE Patient Visibility
      .addCase(togglePatientVisibility.pending, (state) => {
        state.loading = true;
      })
      .addCase(togglePatientVisibility.fulfilled, (state, action) => {
        state.loading = false;
        state.showPatientCount = action.payload;
      })
      .addCase(togglePatientVisibility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔹 ACCEPT Insurance-Covered Orders: show the new value at once, undo it if the save fails
      .addCase(updateAcceptInsuranceOrders.pending, (state, action) => {
        state.acceptInsuranceSaving = true;
        state.acceptInsuranceOrders =
          !!action.meta.arg?.accept_insurance_orders;
      })
      .addCase(updateAcceptInsuranceOrders.fulfilled, (state) => {
        state.acceptInsuranceSaving = false;
      })
      .addCase(updateAcceptInsuranceOrders.rejected, (state, action) => {
        state.acceptInsuranceSaving = false;
        state.acceptInsuranceOrders =
          !action.meta.arg?.accept_insurance_orders;
      })

      // 🔹 UPLOAD Doctor Avatar - Update avatar URL immediately
      .addCase(uploadDoctorAvatar.fulfilled, (state, action) => {
        // Update avatar URL from upload response
        const presignedUrl =
          action.payload?.data?.avatar_presigned_url ||
          action.payload?.avatar_presigned_url ||
          null;

        if (presignedUrl) {
          state.avatarUrl = presignedUrl;
        }
      });
  },
});

export const { setAvatarPreview } = doctorProfileSlice.actions;
export default doctorProfileSlice.reducer;
